/**
 * Console sign-in credentials.
 *
 * The server guards /api with HTTP Basic auth (`guardConsole`), which means
 * every console request has to carry an Authorization header itself — a
 * fetch() never triggers the browser's native login prompt, so without this
 * the console silently 401s on a fresh browser (settings, user management,
 * the live-input token, …).
 *
 * The credential is stored in localStorage so an operator stays signed in
 * across reloads. It is the same bearer secret as the password: a stolen
 * session equals a stolen password, so it is cleared the moment the server
 * rejects it (see `request()` in api.ts) and can be cleared by signing out.
 */

const STORAGE_KEY = "ees.console.credentials";

/** Base64(user:pass) for private-mode browsers where storage is unusable. */
let memoryCredentials: string | null = null;

/** `Basic base64(user:pass)` for the Authorization header, or null when signed out. */
export function authorizationValue(): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return `Basic ${stored}`;
  } catch {
    // Storage unavailable (private mode); fall back to this page's memory.
  }
  return memoryCredentials ? `Basic ${memoryCredentials}` : null;
}

export function setCredentials(username: string, password: string): void {
  const raw = `${username}:${password}`;
  // UTF-8 bytes → binary string → base64, matching the server's
  // Buffer.from(encoded, "base64").toString("utf8") decode.
  const bytes = new TextEncoder().encode(raw);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const encoded = btoa(binary);
  try {
    window.localStorage.setItem(STORAGE_KEY, encoded);
  } catch {
    // No storage: keep the header for this page load only.
    memoryCredentials = encoded;
  }
}

export function clearCredentials(): void {
  memoryCredentials = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
